import Link from "next/link";
import {
  Check,
  Clock3,
  Crown,
} from "lucide-react";

type PricingCardProps = {
  name: string;
  description: string;
  price: string;
  period?: string;
  features: string[];
  buttonLabel: string;
  href?: string;
  highlighted?: boolean;
  primaryAction?: boolean;
  badge?: string;
  current?: boolean;
  disabled?: boolean;
  showPaymentNotice?: boolean;
  compact?: boolean;
  priceNotice?: string;
  featureNotice?: string;
};

export default function PricingCard({
  name,
  description,
  price,
  period,
  features,
  buttonLabel,
  href = "/cadastro",
  highlighted = false,
  primaryAction = false,
  badge,
  current = false,
  disabled = false,
  showPaymentNotice = true,
  compact = false,
  priceNotice,
  featureNotice,
}: PricingCardProps) {
  const isDisabled = current || disabled;
  const renderFeature = (feature: string) => (
    <li key={feature} className="flex items-start gap-3 text-sm leading-5 text-slate-700">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
        <Check size={13} strokeWidth={3} />
      </span>
      <span>{feature}</span>
    </li>
  );

  const buttonClass = [
    "mt-8 inline-flex w-full items-center justify-center gap-2",
    "rounded-2xl px-5 py-3.5 text-sm font-bold transition-all",
    isDisabled
      ? "cursor-not-allowed bg-slate-100 text-slate-400"
      : highlighted
        ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-[0_10px_25px_rgba(79,70,229,0.28)] hover:-translate-y-0.5 hover:opacity-95 hover:shadow-lg"
        : primaryAction
          ? "bg-blue-600 text-white shadow-[0_10px_25px_rgba(37,99,235,0.24)] hover:-translate-y-0.5 hover:bg-blue-700"
          : "border border-slate-300 bg-white text-slate-800 hover:border-blue-600 hover:text-blue-600",
  ].join(" ");

  return (
    <article
      className={`relative flex h-full flex-col ${compact ? "rounded-lg p-5" : "rounded-[28px] p-7"} bg-white transition-all duration-200 ${
        highlighted
          ? "border-2 border-blue-600 shadow-[0_20px_50px_rgba(37,99,235,0.16)] lg:-translate-y-3"
          : current
            ? "border-2 border-emerald-500 shadow-[0_18px_45px_rgba(16,185,129,0.12)]"
            : "border border-slate-200 shadow-[0_14px_35px_rgba(15,23,42,0.07)] hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(15,23,42,0.11)]"
      }`}
    >
      {current ? (
        <span className="absolute left-1/2 top-0 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg">
          <Crown size={13} />
          Plano atual
        </span>
      ) : (
        badge && (
          <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-white shadow-lg">
            {badge}
          </span>
        )
      )}

      <div>
        <h2
          className={`text-xl font-bold ${
            current
              ? "text-emerald-700"
              : highlighted
                ? "text-blue-600"
                : "text-slate-950"
          }`}
        >
          {name}
        </h2>

        <p className="mt-2 min-h-12 text-sm leading-6 text-slate-600">
          {description}
        </p>

        <div className="mt-5 flex items-end gap-1">
          <span className={`${compact ? "text-3xl" : "text-4xl"} font-extrabold tracking-tight text-slate-950`}>
            {price}
          </span>

          {period && (
            <span className="pb-1 text-sm font-medium text-slate-500">
              {period}
            </span>
          )}
        </div>
        {priceNotice && <p className="mt-2 text-xs font-semibold text-slate-500">{priceNotice}</p>}
      </div>

      <ul className="mt-7 flex-1 space-y-3">
        {(compact ? features.slice(0, 5) : features).map(renderFeature)}
        {compact && features.length > 5 && (
          <li>
            <details className="text-sm text-blue-700">
              <summary className="cursor-pointer font-semibold">Ver todos os recursos</summary>
              <ul className="mt-3 space-y-3">{features.slice(5).map(renderFeature)}</ul>
            </details>
          </li>
        )}

        {showPaymentNotice && (
          <li className="flex items-start gap-3 text-sm leading-5 text-slate-400">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Clock3 size={12} strokeWidth={2.5} />
            </span>

            <span>
              Pagamento online{" "}
              <small className="font-semibold text-blue-500">
                em breve
              </small>
            </span>
          </li>
        )}
      </ul>

      {featureNotice && <p className="mt-5 text-sm leading-6 text-slate-600">{featureNotice}</p>}

      {isDisabled ? (
        <span
          aria-disabled="true"
          className={buttonClass}
        >
          {buttonLabel}
        </span>
      ) : (
        <Link
          href={href}
          className={buttonClass}
        >
          {buttonLabel}
        </Link>
      )}
    </article>
  );
}
